// =====================================================
// Main App Component
// =====================================================

import React, { useEffect, useRef } from 'react';
import ChatLauncher from './components/ChatLauncher';
import ChatWindow from './components/ChatWindow';
import { useChatStore } from './store/chatStore';

// Check if running in embedded mode (loaded via iframe with ?embedded=true)
const isEmbedded = new URLSearchParams(window.location.search).get('embedded') === 'true';

function App() {
  const { isOpen, setIsOpen } = useChatStore();

  // Track if chat has been opened at least once (to avoid sending initial false state)
  const hasBeenOpened = useRef(false);

  // Listen for messages from parent window (for embedded mode)
  useEffect(() => {
    if (!isEmbedded) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'ava-chatbot-open') {
        setIsOpen(true);
      }
    };

    window.addEventListener('message', handleMessage);

    // Signal to parent that we're ready to receive messages
    // This fixes the race condition where parent sends 'open' before listener is set up
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'ava-chatbot-ready' }, '*');
    }

    return () => window.removeEventListener('message', handleMessage);
  }, [setIsOpen]);

  // Notify parent window when chat opens/closes so it can resize the container
  // IMPORTANT: Only notify after chat has been opened at least once to prevent
  // initial isOpen:false from closing the chat before it even opens
  useEffect(() => {
    if (window.parent && window.parent !== window) {
      // Track when chat has been opened
      if (isOpen) {
        hasBeenOpened.current = true;
      }

      // Only send state to parent if chat has been opened at least once
      // This prevents the initial isOpen:false from closing the chat
      if (hasBeenOpened.current) {
        window.parent.postMessage({
          type: 'ava-chatbot-state',
          isOpen: isOpen
        }, '*');
      }
    }
  }, [isOpen]);

  // Always show ChatLauncher and ChatWindow
  // The embed script just creates an iframe - React app handles everything
  return (
    <div className="app">
      {/* Only show launcher if NOT embedded (parent page handles launcher in embedded mode) */}
      {!isEmbedded && <ChatLauncher />}
      {isOpen && <ChatWindow />}
    </div>
  );
}

export default App;
